import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CMT_MACHINES,
  PARAM_GROUPS,
  paramsByGroup,
  type MachineShot,
  type MachineStatus,
  type ParamKey,
} from '../data/injectionMachines';
import { useMachineShots } from '../data/machineDataService';
// Reuse the ProcessControlToolPage shell (page pct + body.pct-schnell dark bar,
// breadcrumb, header) so this page matches the placeholder page it replaces.
import './ProcessControlToolPage.css';
import './RealTimeMonitoringPage.css';

const SYSTEM_HOME = '/portal/supplier-process-control/system';

const STATUS_LABEL: Record<MachineStatus, string> = {
  running: 'Running',
  idle: 'Idle',
  down: 'Down',
};

/** Format a numeric value for compact display. */
function fmt(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** Format an epoch-ms timestamp as a wall-clock time. */
function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/* ------------------------------------------------------------------ */
/*  Trend charts                                                       */
/* ------------------------------------------------------------------ */

interface Series {
  key: ParamKey;
  label: string;
  color: string;
}

const AXIS_TICK = { fill: '#64748b', fontSize: 11 };
const GRID_STROKE = 'rgba(148,163,184,0.25)';
const AXIS_LINE = { stroke: 'rgba(148,163,184,0.4)' };
const TOOLTIP_STYLE = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  color: '#1e293b',
  fontSize: 12,
  boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
} as const;

function ChartShell({
  title,
  unit,
  series,
  children,
}: {
  title: string;
  unit: string;
  series: Series[];
  children: React.ReactElement;
}) {
  return (
    <div className="rtm-chart">
      <div className="rtm-chart__head">
        <h3 className="rtm-chart__title">{title}</h3>
        <span className="rtm-chart__unit">{unit}</span>
      </div>
      <div className="rtm-chart__legend">
        {series.map((s) => (
          <span className="rtm-chart__legend-item" key={s.key}>
            <i style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
      <div className="rtm-chart__canvas">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function LineTrend({
  title,
  unit,
  data,
  series,
}: {
  title: string;
  unit: string;
  data: MachineShot[];
  series: Series[];
}) {
  return (
    <ChartShell title={title} unit={unit} series={series}>
      <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
        <CartesianGrid stroke={GRID_STROKE} vertical={false} />
        <XAxis
          dataKey="shotNo"
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={AXIS_LINE}
          minTickGap={24}
        />
        <YAxis
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={AXIS_LINE}
          width={44}
          domain={['auto', 'auto']}
        />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={{ color: '#64748b' }}
          labelFormatter={(v) => `Shot #${v}`}
        />
        {series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ChartShell>
  );
}

function AreaTrend({
  title,
  unit,
  data,
  series,
}: {
  title: string;
  unit: string;
  data: MachineShot[];
  series: Series;
}) {
  return (
    <ChartShell title={title} unit={unit} series={[series]}>
      <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
        <defs>
          <linearGradient id="rtmArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={series.color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={series.color} stopOpacity={0.03} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={GRID_STROKE} vertical={false} />
        <XAxis
          dataKey="shotNo"
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={AXIS_LINE}
          minTickGap={24}
        />
        <YAxis
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={AXIS_LINE}
          width={44}
          domain={['auto', 'auto']}
        />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={{ color: '#64748b' }}
          labelFormatter={(v) => `Shot #${v}`}
        />
        <Area
          type="monotone"
          dataKey={series.key}
          name={series.label}
          stroke={series.color}
          strokeWidth={2}
          fill="url(#rtmArea)"
          isAnimationActive={false}
        />
      </AreaChart>
    </ChartShell>
  );
}

/* ------------------------------------------------------------------ */
/*  Dashboard (right side) — wired to the live shot stream            */
/* ------------------------------------------------------------------ */

function Dashboard({ machineId }: { machineId: string }) {
  const { shots, latest, status, isLive } = useMachineShots(machineId);
  const machine = CMT_MACHINES.find((m) => m.id === machineId);

  if (status === 'loading' || !latest) {
    return (
      <div className="rtm__state">
        <span className="rtm__spinner" aria-hidden />
        <p>Connecting to live shot stream…</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="rtm__state rtm__state--error">
        <p>Live data stream is unavailable right now. Please try again.</p>
      </div>
    );
  }

  const recent = shots.slice().reverse().slice(0, 12);

  return (
    <>
      {/* Head: mold / latest shot / status */}
      <div className="rtm-dash__head">
        <div className="rtm-dash__mold">
          <span className="rtm-dash__mold-label">Mold</span>
          <span className="rtm-dash__mold-name">{latest.moldName}</span>
        </div>
        <div className="rtm-dash__meta">
          <div className="rtm-dash__meta-item">
            <span className="rtm-dash__meta-label">Latest Shot</span>
            <span className="rtm-dash__meta-value">#{latest.shotNo}</span>
          </div>
          <div className="rtm-dash__meta-item">
            <span className="rtm-dash__meta-label">Time</span>
            <span className="rtm-dash__meta-value">{fmtTime(latest.shotTime)}</span>
          </div>
          {machine && (
            <div className="rtm-dash__meta-item">
              <span className="rtm-dash__meta-label">Status</span>
              <span
                className={`rtm-dash__status rtm-dash__status--${machine.status}`}
              >
                <i aria-hidden />
                {STATUS_LABEL[machine.status]}
              </span>
            </div>
          )}
          {isLive && (
            <span className="rtm-dash__live">
              <i aria-hidden />
              LIVE
            </span>
          )}
        </div>
      </div>

      {/* KPI cards by group */}
      <div className="rtm-kpis">
        {PARAM_GROUPS.map((group) => (
          <section className="rtm-kpi-group" key={group.id}>
            <h3 className="rtm-kpi-group__title">{group.label}</h3>
            <div className="rtm-kpi-group__grid">
              {paramsByGroup(group.id).map((param) => (
                <div className="rtm-kpi" key={param.key}>
                  <span className="rtm-kpi__label">{param.label}</span>
                  <span className="rtm-kpi__value">
                    {fmt(latest[param.key])}
                    <span className="rtm-kpi__unit">{param.unit}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Trend charts */}
      <div className="rtm-charts">
        <LineTrend
          title="Injection & Peak Pressure"
          unit="bar"
          data={shots}
          series={[
            { key: 'injectionPressure', label: 'Injection', color: '#ea6a0a' },
            { key: 'peakPressure', label: 'Peak', color: '#f59e0b' },
          ]}
        />
        <LineTrend
          title="Mold Temperature (Front / Rear)"
          unit="°C"
          data={shots}
          series={[
            { key: 'moldTempFront', label: 'Front', color: '#ea6a0a' },
            { key: 'moldTempRear', label: 'Rear', color: '#0ea5e9' },
          ]}
        />
        <AreaTrend
          title="Cycle Time"
          unit="s"
          data={shots}
          series={{ key: 'cycleTime', label: 'Cycle Time', color: '#f5811f' }}
        />
      </div>

      {/* Recent shots table */}
      <div className="rtm-table-card">
        <h3 className="rtm-table-card__title">Recent Shots</h3>
        <div className="rtm-table-wrap">
          <table className="rtm-table">
            <thead>
              <tr>
                <th className="rtm-table__sticky">Shot #</th>
                <th>Time</th>
                {PARAM_GROUPS.flatMap((g) =>
                  paramsByGroup(g.id).map((p) => (
                    <th key={p.key}>
                      {p.label}
                      <span className="rtm-table__unit"> ({p.unit})</span>
                    </th>
                  )),
                )}
              </tr>
            </thead>
            <tbody>
              {recent.map((shot, idx) => (
                <tr key={shot.id} className={idx === 0 ? 'rtm-table__row--new' : ''}>
                  <td className="rtm-table__sticky rtm-table__strong">
                    {shot.shotNo}
                  </td>
                  <td>{fmtTime(shot.shotTime)}</td>
                  {PARAM_GROUPS.flatMap((g) =>
                    paramsByGroup(g.id).map((p) => (
                      <td key={p.key}>{fmt(shot[p.key])}</td>
                    )),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

/**
 * CMT · Real Time Process Monitoring.
 *
 * Reuses the ProcessControlToolPage shell (light `page pct` body + the
 * `body.pct-schnell` dark/orange top bar, breadcrumb and title). Only the
 * placeholder block is replaced: a left rail with an enlarged machine photo +
 * "Machine No." selector (two CMT cells), and a right-side live dashboard.
 */
export function RealTimeMonitoringPage() {
  const [selectedId, setSelectedId] = useState<string>(CMT_MACHINES[0].id);

  // Match the placeholder page's Schnell-style dark top bar (scoped to mount).
  useEffect(() => {
    document.body.classList.add('pct-schnell');
    return () => document.body.classList.remove('pct-schnell');
  }, []);

  return (
    <div className="page pct">
      {/* Breadcrumb */}
      <nav className="pct-crumbs" aria-label="Breadcrumb">
        <Link to={SYSTEM_HOME} className="pct-crumbs__link">
          Process Control System
        </Link>
        <span className="pct-crumbs__sep" aria-hidden>
          /
        </span>
        <Link to={SYSTEM_HOME} className="pct-crumbs__link">
          CMT
        </Link>
        <span className="pct-crumbs__sep" aria-hidden>
          /
        </span>
        <span className="pct-crumbs__current" aria-current="page">
          Real Time Process Monitoring
        </span>
      </nav>

      {/* Title */}
      <header className="pct-header">
        <h1 className="pct-header__title">CMT · Real Time Process Monitoring</h1>
        <p className="pct-header__subtitle">
          Process Control System — CMT factory · live shot-by-shot process
          parameters
        </p>
      </header>

      {/* Monitoring area (replaces the placeholder block) */}
      <section className="rtm">
        <div className="rtm__layout">
          <aside className="rtm__machines" aria-label="Machine selector">
            {CMT_MACHINES.map((machine) => {
              const selected = machine.id === selectedId;
              return (
                <button
                  key={machine.id}
                  type="button"
                  className={`rtm-machine${selected ? ' is-selected' : ''}`}
                  aria-pressed={selected}
                  onClick={() => setSelectedId(machine.id)}
                >
                  <div className="rtm-machine__img">
                    <img src={machine.image} alt="" loading="lazy" />
                  </div>
                  <div className="rtm-machine__info">
                    <span className="rtm-machine__no-label">Machine No.</span>
                    <span className="rtm-machine__no">{machine.machineNo}</span>
                    <span
                      className={`rtm-machine__status rtm-machine__status--${machine.status}`}
                    >
                      <i className="rtm-machine__dot" aria-hidden />
                      {STATUS_LABEL[machine.status]}
                    </span>
                  </div>
                </button>
              );
            })}
          </aside>

          <section className="rtm__dashboard" aria-live="polite">
            {/* Remount on machine change so the stream fully resets. */}
            <Dashboard machineId={selectedId} key={selectedId} />
          </section>
        </div>
      </section>
    </div>
  );
}
